import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { BehaviorSubject, Observable } from 'rxjs';
import { AgmMarkerComponent } from './agm-marker.component';
import { AgmPolygonComponent } from './agm-polygon.component';
import { AgmCircleComponent } from './agm-circle.component';

@Component({
  selector: 'agm-map',
  host: { '[attr.data-unique-id]': 'uniqueId' },
  template: `
    <div
      class="agm-map-container-inner sebm-google-map-container-inner"
      [ngStyle]="{ width: width, position: 'relative', overflow: 'hidden' }"
    >
      <google-map
        #map
        [center]="{ lat: latitude, lng: longitude }"
        [height]="height"
        [mapTypeId]="mapTypeId"
        [options]="mapOptions"
        [width]="width"
        [zoom]="zoom"
        (boundsChanged)="handleBoundsChange()"
        (centerChanged)="handleCenterChanged()"
        (mapClick)="handleMapClick($event)"
        (tilesloaded)="onTilesloaded()"
      >
      </google-map>
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
      google-map {
        width: 100%;
        height: 100%;
      }
      .agm-map-container-inner {
        width: inherit;
        height: inherit;
      }
    `,
  ],
  standalone: false,
})
export class AgmMapComponent
  implements OnInit, AfterViewInit, AfterContentInit, OnDestroy, OnChanges
{
  uniqueId = `obj-${Math.random().toString(36).substr(2, 9)}`; // Genera un ID unico
  @HostBinding('class') className = 'sebm-google-map-container';
  @ViewChild('map', { static: false }) map: GoogleMap;
  @ContentChildren(AgmMarkerComponent) markers!: QueryList<AgmMarkerComponent>;
  @ContentChildren(AgmPolygonComponent) polygons!: QueryList<AgmPolygonComponent>;
  //@ContentChildren(AgmPolylineComponent) polylines!: QueryList<AgmPolylineComponent>;
  @ContentChildren(AgmCircleComponent) circles!: QueryList<AgmCircleComponent>;

  @Input() height: string = '100%';
  @Input() width: string = '100%';
  @Input() latitude: number = 0;
  @Input() longitude: number = 0;
  @Input() zoom: number = 8;
  @Input() mapTypeId: google.maps.MapTypeId | undefined;
  @Input() fullscreenControl: boolean = false;
  @Input() scrollwheel: boolean = true;
  @Input() streetViewControl: boolean = false;
  @Input() scaleControl: boolean = true;
  @Input() mapDraggable: boolean = true;
  @Input() zoomControl: boolean = true;
  @Input() minZoom: number;
  @Input() maxZoom: number;
  @Input() stylesOptions: google.maps.MapTypeStyle[];
  @Input() zoomControlOptions?: google.maps.ZoomControlOptions; // Aggiungi questo input
  @Input() disableDoubleClickZoom: boolean = false;
  @Input() disableDefaultUI: boolean = false;
  @Input() mapTypeControl: boolean = false;
  @Input() rotateControl: boolean = false;
  @Input() tiltControl: boolean = false;
  @Input() fitBounds: google.maps.LatLngBounds | null = null;
  @Input() disableFitBounds = false;
  @Input() allowMultipleInfoWindows: boolean = false;

  @Output() mapClick = new EventEmitter<google.maps.MapMouseEvent>();
  @Output() tilesloaded = new EventEmitter<void>();
  @Output() boundsChange = new EventEmitter<google.maps.LatLngBounds | null>();
  @Output() mapReady = new EventEmitter<google.maps.Map>();

  private mapReady$ = new BehaviorSubject<boolean>(false);
  //private openInfoWindows: Array<AgmMarkerComponent | AgmPolygonComponent | AgmCircleComponent | AgmPolylineComponent> = [];
  private openInfoWindows: Array<AgmMarkerComponent | AgmPolygonComponent | AgmCircleComponent> =
    [];
  private boundsLocked = false;

  mapOptions: google.maps.MapOptions;

  @Output() click = new EventEmitter<void>();
  @Output() centerChange = new EventEmitter<google.maps.LatLng>();

  private markerClusterer!: MarkerClusterer;
  private addedMarkers: google.maps.Marker[] = [];
  private previousPolygonsHash = '';
  private previousPolygons: AgmPolygonComponent[] = [];
  private previousCircles: AgmCircleComponent[] = [];

  ngOnInit(): void {
    this.mapOptions = {
      fullscreenControl: this.fullscreenControl,
      scrollwheel: this.scrollwheel,
      streetViewControl: this.streetViewControl,
      scaleControl: this.scaleControl,
      draggable: this.mapDraggable,
      zoomControl: this.zoomControl,
      disableDoubleClickZoom: this.disableDoubleClickZoom,
      disableDefaultUI: this.disableDefaultUI,
      mapTypeControl: this.mapTypeControl,
      rotateControl: this.rotateControl,
      tiltInteractionEnabled: this.tiltControl,
      zoomControlOptions: this.zoomControlOptions,
      minZoom: this.minZoom || null,
      maxZoom: this.maxZoom || null,
      styles: this.stylesOptions || null,
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fitBounds']?.currentValue && this.map?.googleMap) {
      this.map.googleMap.fitBounds(this.fitBounds);
    }
  }

  ngAfterViewInit() {
    if (this.map && this.map.googleMap) {
      const mapInstance = this.map.googleMap!;
      this.mapReady.emit(mapInstance);
      this.mapReady$.next(true);
      this.initializeClusterer(mapInstance);
      this.initializeMapElements();
    }
  }

  ngAfterContentInit() {
    this.markers.changes.subscribe(() => {
      this.clearMarkers();
      this.addMarkers();
    });

    this.polygons.changes.subscribe(() => {
      this.clearPolygons();
      this.previousPolygons = this.polygons.toArray();
      this.addPolygons();
    });

    this.circles.changes.subscribe(() => {
      this.clearCircles();
      this.previousCircles = this.circles.toArray();
      this.addCircles();
    });
  }

  private initializeMapElements() {
    if (this.map && this.map.googleMap) {
      // Inizializzazione deii listener
      this.setupMapListeners();

      // Se non ci sono elementi, interrompi l'inizializzazione
      if (!this.markers.length && !this.polygons.length && !this.circles.length) {
        return;
      }
      // Inizializzazione dei Polygons
      this.addPolygons();

      // Inizializzazione dei Markers per markerClusterer
      this.addMarkers();
      // Inizializzazione dei Circles
      this.addCircles();
    }
  }

  private initializeClusterer(mapInstance: google.maps.Map) {
    if (!this.markerClusterer) {
      const clusterStyles = [
        {
          url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png',
          textColor: 'black',
          textSize: 12,
        },
        {
          url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m2.png',
          textColor: 'black',
          textSize: 12,
        },
        {
          url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m3.png',
          textColor: 'black',
          textSize: 12,
        },
        {
          url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m4.png',
          textColor: 'black',
          textSize: 12,
        },
      ];

      this.markerClusterer = new MarkerClusterer({
        map: mapInstance,
        markers: [],
        algorithm: new SuperClusterAlgorithm({
          radius: 115, // Ex gridSize
          minPoints: 2, // Numero minimo di marker per formare un cluster
        }),
        renderer: {
          render: ({ count, position }) => {
            const styleIndex = count > 1000 ? 3 : count > 100 ? 2 : count > 10 ? 1 : 0; // Soglie personalizzate
            const style = clusterStyles[styleIndex];
            return new google.maps.Marker({
              position,
              label: {
                text: `${count}`,
                color: style.textColor,
                fontWeight: 'bold',
                fontSize: `${style.textSize}px`,
              },
              icon: {
                url: style.url,
              },
            });
          },
        },
      });
    }
  }

  /**
   * Configura gli eventi della mappa per bloccare il ricalcolo dei bounds quando necessario.
   */
  private setupMapListeners() {
    if (!this.map || !this.map.googleMap) {
      return;
    }
    const mapInstance = this.map.googleMap;

    mapInstance.addListener('dragstart', () => {
      this.boundsLocked = true; // Blocca il ricalcolo dei bounds dopo l'interazione
    });

    mapInstance.addListener('zoom_changed', () => {
      this.boundsLocked = true; // Blocca il ricalcolo dei bounds durante lo zoom
    });
  }

  /**
   * Inizializza e aggiunge i marker sulla mappa.
   */
  private addMarkers() {
    if (!this.map || !this.map.googleMap) {
      return;
    }
    const mapInstance = this.map.googleMap;

    if (!this.markers.length) return;

    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers(); // Svuota i marker dal clusterer
    }
    this.markers.forEach((marker) => {
      marker.initialize(mapInstance);
      marker.markerClick.subscribe(() => {
        this.handleElementClick(marker);
      });

      const googleMarker = marker.getGoogleMarker();
      if (googleMarker) {
        this.addedMarkers.push(googleMarker);
      }
    });

    // Aggiungi i marker al clusterer
    if (this.markerClusterer && this.addedMarkers.length) {
      this.markerClusterer.addMarkers(this.addedMarkers);
    }

    // Aggiorno il bounce per la mappa
    this.updateMarkersBounds();
  }

  /**
   * Inizializza e aggiunge i poligoni sulla mappa.
   */
  private addPolygons() {
    if (!this.map || !this.map.googleMap) {
      return;
    }
    const mapInstance = this.map.googleMap;

    if (!this.polygons.length) return;

    this.polygons.forEach((polygon) => {
      polygon.initialize(mapInstance);
      polygon.polyClick.subscribe(() => {
        this.handleElementClick(polygon);
      });
    });

    // Aggiorno il bounce per la mappa
    this.updatePolygonsBounds();
  }

  /**
   * Inizializza e aggiunge i cerchi sulla mappa.
   */
  private addCircles() {
    if (!this.map || !this.map.googleMap) {
      return;
    }
    const mapInstance = this.map.googleMap;

    if (!this.circles.length) return;

    this.circles.forEach((circle) => {
      circle.initialize(mapInstance);
      circle.circleClick.subscribe(() => {
        this.handleElementClick(circle);
      });
    });
    // Aggiorno il bounce per la mappa
    this.updateCirclesBounds();
  }

  /**
   * Rimuove tutti gli elementi dalla mappa
   */
  private clearMapElements() {
    this.clearMarkers();
    this.clearPolygons();
    this.clearCircles();
  }

  /**
   * Rimuove tutti i marker dalla mappa e dal clusterer.
   */
  private clearMarkers() {
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers(); // Svuota i marker dal clusterer
    }

    this.addedMarkers.forEach((marker) => marker.setMap(null));
    this.addedMarkers = [];
  }

  /**
   * Rimuove tutti i poligoni dalla mappa utilizzando la lista precedente.
   */
  private clearPolygons() {
    if (this.previousPolygons.length > 0) {
      this.previousPolygons.forEach((polygon) => {
        if (polygon.googlePolygon) {
          polygon.remove();
        } else {
        }
      });
      this.previousPolygons = [];
    }
  }

  /**
   * Rimuove tutti i cerchi dalla mappa utilizzando la lista precedente.
   */
  private clearCircles() {
    if (this.previousCircles.length > 0) {
      this.previousCircles.forEach((circle) => {
        if (circle.getCenter()) {
          circle.remove();
        } else {
        }
      });
      this.previousCircles = [];
    }
  }

  handleCenterChanged() {
    if (this.map && this.map.googleMap) {
      this.centerChange.emit(this.map.googleMap.getCenter());
    }
  }

  private updateMarkersBounds() {
    if (!this.map || !this.map.googleMap || this.boundsLocked) {
      return;
    }

    // 1. Controlla se è stato passato `fitBounds` come input
    if (this.fitBounds) {
      this.map.googleMap.fitBounds(this.fitBounds); // Usa i bounds passati come input
      return;
    }

    // Controlla `disableFitBounds` come input
    if (this.disableFitBounds) {
      this.boundsLocked = true;
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    this.extendBoundsWithMarkers(bounds);

    // 3. Applica i bounds calcolati alla mappa
    if (!bounds.isEmpty()) {
      this.map.googleMap.fitBounds(bounds);
    }
  }

  private updatePolygonsBounds() {
    if (!this.map || !this.map.googleMap || this.boundsLocked) {
      return;
    }

    // 1. Controlla se è stato passato `fitBounds` come input
    if (this.fitBounds) {
      this.map.googleMap.fitBounds(this.fitBounds); // Usa i bounds passati come input
      return;
    }

    // Controlla `disableFitBounds` come input
    if (this.disableFitBounds) {
      this.boundsLocked = true;
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    this.extendBoundsWithPolygons(bounds);

    // 3. Applica i bounds calcolati alla mappa
    if (!bounds.isEmpty()) {
      this.map.googleMap.fitBounds(bounds);
    }
  }

  private updateCirclesBounds() {
    if (!this.map || !this.map.googleMap || this.boundsLocked) {
      return;
    }

    // 1. Controlla se è stato passato `fitBounds` come input
    if (this.fitBounds) {
      this.map.googleMap.fitBounds(this.fitBounds); // Usa i bounds passati come input
      return;
    }

    // Controlla `disableFitBounds` come input
    if (this.disableFitBounds) {
      this.boundsLocked = true;
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    this.extendBoundsWithCircles(bounds);

    // 3. Applica i bounds calcolati alla mappa
    if (!bounds.isEmpty()) {
      this.map.googleMap.fitBounds(bounds);
    }
  }

  private extendBoundsWithMarkers(bounds: google.maps.LatLngBounds) {
    this.markers.forEach((marker) => {
      const position = marker.getGoogleMarker()?.getPosition();
      if (position) {
        bounds.extend(position);
      }
    });
  }

  private extendBoundsWithPolygons(bounds: google.maps.LatLngBounds) {
    this.polygons.forEach((polygon) => {
      const paths = polygon.paths || [];
      if (Array.isArray(paths[0])) {
        paths[0].forEach((latLng: google.maps.LatLngLiteral) => bounds.extend(latLng));
      } else {
        paths.forEach((latLng: google.maps.LatLngLiteral) => bounds.extend(latLng));
      }
    });
  }

  private extendBoundsWithCircles(bounds: google.maps.LatLngBounds) {
    this.circles.forEach((circle) => {
      const center = circle.getCenter();
      const radius = circle.getRadius();

      if (center) {
        bounds.extend(center);

        // Calcola i punti ai limiti del raggio
        const northEast = google.maps.geometry.spherical.computeOffset(center, radius, 45);
        const southWest = google.maps.geometry.spherical.computeOffset(center, radius, 225);
        bounds.extend(northEast);
        bounds.extend(southWest);
      }
    });
  }

  public triggerMapReady(): Observable<any> {
    return this.mapReady$.asObservable();
  }

  handleMapClick(event: google.maps.MapMouseEvent): void {
    this.mapClick.emit(event);
  }

  onTilesloaded(): void {
    this.tilesloaded.emit();
  }

  handleBoundsChange(): void {
    if (this.map && this.map.googleMap) {
      const bounds = this.map.googleMap.getBounds();
      this.boundsChange.emit(bounds);
    }
  }

  //private handleElementClick(element: AgmMarkerComponent | AgmPolygonComponent | AgmCircleComponent | AgmPolylineComponent): void {
  private handleElementClick(
    element: AgmMarkerComponent | AgmPolygonComponent | AgmCircleComponent
  ): void {
    // Controllo se l'elemento ha `enableInfoWindow === true`
    if ('enableInfoWindow' in element && !element.enableInfoWindow) {
      return; // Se `enableInfoWindow` è `false`, non faccio nulla
    }

    if (!this.allowMultipleInfoWindows) {
      this.openInfoWindows.forEach((openElement) => {
        if (openElement !== element) {
          openElement.closeInfoWindow();
        }
      });
      this.openInfoWindows = [element];
    } else {
      if (!this.openInfoWindows.includes(element)) {
        this.openInfoWindows.push(element);
      }
    }

    element.openInfoWindow(); // Ora l'info window si apre solo se `enableInfoWindow === true`
  }

  ngOnDestroy(): void {
    if (this.map && this.map.googleMap) {
      google.maps.event.clearInstanceListeners(this.map.googleMap);
    }
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers(); // Rimuove tutti i marker dal clusterer
    }
    this.mapReady$.complete();
    this.detachElements();
  }

  private detachElements() {
    // Rimuovi i marker
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }
    this.addedMarkers.forEach((marker) => marker.setMap(null));

    // Rimuovi i poligoni
    this.polygons.forEach((polygon) => polygon.remove());

    // Rimuovi i cerchi
    this.circles.forEach((circle) => circle.remove());

    // Svuota le liste
    this.addedMarkers = [];
    this.markers.reset([]);
    this.polygons.reset([]);
    this.circles.reset([]);
    this.previousPolygons = [];
    this.previousCircles = [];
  }
}
